import React, { useEffect } from 'react';
import { ReactModel } from '../../react-model/react-model';
import { useOnModelChange } from '../hooks/use-on-model-change';
import { useValidator } from '../hooks/use-validator';

type TSelectOptionItem<T = any> = {
	label?: string; //default to "value"
	value: T;
};

export type TSelectOptionItems<T> = (TSelectOptionItem<T> | string)[];

export interface HSelectProps<T> extends React.SelectHTMLAttributes<any> {
	model: ReactModel<T>;
	/** if "multiple={true}" in the the property, then the field value is an array */
	field?: keyof NonNullable<T>;
	/** options, format: {label,value}[] */
	field_options?: keyof T;
	/** Set the component properties when then model changes.
	 * - to set UI selection, use `value`
	 * - to set UI options, use `options`
	 */
	onModelChange?: (state: T) => void | (React.SelectHTMLAttributes<any> & {options?: TSelectOptionItems<any>});
	/** Set the model when then input element changes
	 * - It behaves the same like setState((state)=>{...})
	 * - The current selection is in parameter `value`
	*/
	onViewChange?: (state: T, value: any) => void | T;

	/** if the element is a string, then it will be converted to object {value:string}[] */
	options?: TSelectOptionItems<any>;

	no_popover?: boolean;

	/** The word "ref" is reserved. We have to use ref2 */
	ref2?: { current: HTMLSelectElement | null };
	//onChange?:(e:React.ChangeEvent<HTMLInputElement>)=>void,
}

export function HSelect<T>({ model, field, field_options, options, ref2, no_popover, onModelChange, onViewChange, ...props }: HSelectProps<T>) {
	if (!ref2) ref2 = { current: null };
	if(field !== undefined){
		var { state: value_in_model, setState: setValue } = model.useModel(field as any);
		//add validator
		props = useValidator(model, field as any, ref2, props, no_popover);
		//"onModelChange" may update and then merge the "value" field
		props.value = value_in_model === undefined ? '' : (value_in_model as any);
	}else{
		props = {...props};//unfreeze the object for useOnModelChange
	}

	if (field_options) {
		const { state: options_in_model } = model.useModel(field_options);
		if (options_in_model) {
			options = options_in_model as TSelectOptionItems<any>;
		}
	}

	useOnModelChange(model, onModelChange, props);
	//use "options", which may be returned by onModelChange
	if((props as any).options){
		options = (props as any).options;
		delete (props as any).options;
	}
	//fix invalid value
	useEffect(() => {
		//Fix issue: the first option is selected when the value is invalid
		if (ref2 && ref2.current) ref2.current.value = props.value as any;// value2 as any;
	});

	if(field === undefined && ! onModelChange){
		console.error('(HS60)');//console.error('field or onModelChange expected');
	}

	// console.log('props.value', props.value);
	// console.log('options: ', options)
	return (
		<>
			<select
				{...props}
				// value={value2 === undefined ? '' : (value2 as any)}
				ref={ref2}
				onChange={(e) => {
					if(field !== undefined){
						if(props.multiple){
							setValue?.(getSelectValues(e.currentTarget) as any);
						}else{
							setValue?.(e.currentTarget.value as any);
						}
						model.validator(false)?.enable(field as any, true);
					}
					if(onViewChange){
						model.setState((state)=>{
							return onViewChange(state, e.currentTarget.value);
						})
					}
				}}
			>
				{options?.map((x, idx) => {
					const t = typeof x;
					const is_obj = typeof x === 'object';
					return (
						<option key={idx} value={is_obj ? x.value : x}>
							{is_obj ? x.label || x.value : x}
						</option>
					);
				})}
			</select>
		</>
	);
}


function getSelectValues(select:HTMLSelectElement) {
    // if('target' in select){
    //     select = select.target as HTMLSelectElement;
    // }
    return Array.from(select.selectedOptions, (v)=>v.value)
}
